import { useProfile } from '@app/app/_context/ProfileContext/useProfile';
import { Address } from '@app/domain/model/Address';
import { VehicleOwnerFormData } from '@app/domain/model/eshop/transport/VehicleOwnerFormData';
import { TransportInsuranceRequestResult } from '@app/service/eshop/transport/actions/requestTransportInsurance';
import { useCallback, useEffect, useState } from 'react';

export const useOwnerFormFields = (requestResult: TransportInsuranceRequestResult, isCompany: boolean) => {
    const profile = useProfile();

    const getInitialOwnerAddress = useCallback((): Address | null => {
        const profileAddress = profile?.mainAddress || profile?.addresses[0];

        if (!profileAddress) {
            return null;
        }

        const hasRequiredFields =
            profileAddress.country &&
            profileAddress.street &&
            profileAddress.houseNumber &&
            profileAddress.apartmentNumber &&
            profileAddress.city &&
            profileAddress.postalCode &&
            profileAddress.municipality;

        return hasRequiredFields ? profileAddress : null;
    }, [profile]);

    const isOwnerLoggedInUser = profile?.personalCode === requestResult.personalCode;

    const extractPhoneNumber = useCallback((phone?: string): string => {
        if (!phone) return '';

        const cleaned = phone.replace(/\D/g, '');
        if (cleaned.startsWith('370')) {
            return cleaned.slice(3);
        }

        return cleaned;
    }, []);

    const formatLithuanianPhone = useCallback((value: string): string => {
        const digits = value.replace(/\D/g, '');

        const limited = digits.slice(0, 8);

        if (limited.length <= 3) {
            return limited;
        } else if (limited.length <= 5) {
            return `${limited.slice(0, 3)} ${limited.slice(3)}`;
        } else {
            return `${limited.slice(0, 3)} ${limited.slice(3, 5)} ${limited.slice(5)}`;
        }
    }, []);

    const getOwnerFirstName = useCallback(() => {
        return isCompany ? (profile?.name ?? '') : (profile?.firstName ?? '');
    }, [isCompany, profile?.name, profile?.firstName]);

    const getOwnerLastName = useCallback(() => {
        return isCompany ? '' : (profile?.name ?? '');
    }, [isCompany, profile?.name]);

    const getOwnerEmail = useCallback(() => {
        return profile?.mainEmail ?? profile?.emails?.[0]?.email ?? '';
    }, [profile?.mainEmail, profile?.emails]);

    const [ownerFormData, setOwnerFormData] = useState<VehicleOwnerFormData>({
        ownerFirstName: isOwnerLoggedInUser ? getOwnerFirstName() : '',
        ownerLastName: isOwnerLoggedInUser ? getOwnerLastName() : '',
        ownerPersonalCode: requestResult.personalCode,
        phoneNumber: isOwnerLoggedInUser
            ? formatLithuanianPhone(extractPhoneNumber(profile?.mainPhone ?? profile?.phones?.[0]?.phone))
            : '',
        email: isOwnerLoggedInUser ? getOwnerEmail() : '',
        emailConfirmation: '',
        ownerAddress: isOwnerLoggedInUser ? getInitialOwnerAddress() : null,
        insuredByOwner: true,
        payerFirstName: '',
        payerLastName: '',
        payerPersonalCode: '',
        payerPhoneNumber: '',
        payerEmail: '',
        payerEmailConfirmation: '',
        payerAddress: null,
    });

    useEffect(() => {
        if (profile) {
            const isOwner = profile.personalCode === requestResult.personalCode;

            setOwnerFormData(prev => ({
                ...prev,
                ownerFirstName: isOwner ? getOwnerFirstName() : prev.ownerFirstName,
                ownerLastName: isOwner ? getOwnerLastName() : prev.ownerLastName,
                ownerPersonalCode: requestResult.personalCode,
                phoneNumber: isOwner
                    ? formatLithuanianPhone(extractPhoneNumber(profile.mainPhone ?? profile.phones?.[0]?.phone))
                    : prev.phoneNumber,
                ownerAddress: isOwner ? getInitialOwnerAddress() : prev.ownerAddress,
                email: isOwner ? getOwnerEmail() : prev.email,
                insuredByOwner: true,
                payerFirstName: '',
                payerLastName: '',
                payerPersonalCode: '',
                payerPhoneNumber: '',
                payerEmail: '',
                payerAddress: null,
            }));
        }
    }, [
        getInitialOwnerAddress,
        extractPhoneNumber,
        formatLithuanianPhone,
        profile,
        requestResult.personalCode,
        getOwnerFirstName,
        getOwnerLastName,
        getOwnerEmail,
    ]);

    const handleInputChange = useCallback((field: keyof VehicleOwnerFormData, value: string | boolean | null) => {
        setOwnerFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const onChangeOwnerAddress = useCallback((address: Address | null) => {
        setOwnerFormData(prev => ({
            ...prev,
            ownerAddress: address,
        }));
    }, []);

    const onChangePayerAddress = useCallback((address: Address | null) => {
        setOwnerFormData(prev => ({
            ...prev,
            payerAddress: address,
        }));
    }, []);

    const handlePhoneNumberField = useCallback(
        (field: 'phoneNumber' | 'payerPhoneNumber') => {
            return (event: React.ChangeEvent<HTMLInputElement>) => {
                const formattedValue = formatLithuanianPhone(event.target.value);
                handleInputChange(field, formattedValue);
            };
        },
        [formatLithuanianPhone, handleInputChange],
    );

    const handleInputField = useCallback(
        (field: keyof VehicleOwnerFormData) => {
            return (event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
                handleInputChange(field, value);
            };
        },
        [handleInputChange],
    );

    const updateFormData = useCallback((updates: Partial<VehicleOwnerFormData>) => {
        setOwnerFormData(prev => ({ ...prev, ...updates }));
    }, []);

    const resetForm = useCallback(() => {
        const isOwner = profile?.personalCode === requestResult.personalCode;

        setOwnerFormData({
            ownerFirstName: isOwner ? getOwnerFirstName() : '',
            ownerLastName: isOwner ? getOwnerLastName() : '',
            ownerPersonalCode: requestResult.personalCode ?? '',
            phoneNumber: isOwner
                ? formatLithuanianPhone(extractPhoneNumber(profile?.mainPhone ?? profile?.phones?.[0]?.phone))
                : '',
            email: isOwner ? getOwnerEmail() : '',
            emailConfirmation: '',
            ownerAddress: isOwner ? getInitialOwnerAddress() : null,
            insuredByOwner: true,
            payerFirstName: '',
            payerLastName: '',
            payerPersonalCode: '',
            payerPhoneNumber: '',
            payerEmail: '',
            payerEmailConfirmation: '',
            payerAddress: null,
        });
    }, [
        profile,
        requestResult,
        getInitialOwnerAddress,
        extractPhoneNumber,
        formatLithuanianPhone,
        getOwnerFirstName,
        getOwnerLastName,
        getOwnerEmail,
    ]);

    return {
        ownerFormData,

        handleInputField,
        handlePhoneNumberField,
        handleInputChange,
        updateFormData,
        resetForm,
        setOwnerFormData,

        onChangeOwnerAddress,
        onChangePayerAddress,
    };
};
